import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, DownloadIcon, ExternalLinkIcon, FileTextIcon } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { dictionary } from '../lib/content';
export function Papers() {
  const {
    lang,
    t
  } = useI18n();
  const items = dictionary[lang].papers.items;
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const allTags = useMemo(() => {
    const set = new Set<string>();
    items.forEach(p => p.tags.forEach(tag => set.add(tag)));
    return Array.from(set);
  }, [items]);
  const filtered = items.filter(p => {
    const matchesQuery = p.title.toLowerCase().includes(query.toLowerCase());
    const matchesTag = !activeTag || p.tags.includes(activeTag);
    return matchesQuery && matchesTag;
  });
  return <section className="w-full bg-[#FAFAFA] py-32 px-6 md:px-12 border-t border-black/[0.06]" data-id="element-178">
      <div className="max-w-6xl mx-auto" data-id="element-179">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="font-display text-4xl md:text-5xl text-[#0A0A0F] mb-12 tracking-tight" data-id="element-180">
          {t('papers.title')}
        </motion.h2>

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10" data-id="element-181">
          <div className="relative flex-1 max-w-md" data-id="element-182">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A95]" aria-hidden="true" data-id="element-183" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder={t('papers.search')} className="w-full pl-10 pr-4 py-3 bg-white border border-black/[0.08] rounded-lg text-[#0A0A0F] text-sm placeholder-[#8A8A95] focus:outline-none focus:border-violet-electric/50 transition" data-id="element-184" />
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-185">
            {allTags.map(tag => <button key={tag} onClick={() => setActiveTag(activeTag === tag ? null : tag)} className={`px-3 py-1.5 text-xs rounded-full border transition ${activeTag === tag ? 'bg-violet-electric/10 border-violet-electric/40 text-violet-deep' : 'bg-white border-black/[0.08] text-[#4A4A55] hover:border-black/20 hover:text-[#0A0A0F]'}`} data-id="element-186">
                {tag}
              </button>)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-187">
          {filtered.map((paper, i) => <motion.article key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.05
        }} className="group p-7 bg-white border border-black/[0.06] rounded-xl hover:border-violet-electric/40 hover:shadow-md transition" data-id="element-188">
              <div className="flex items-start justify-between mb-4" data-id="element-189">
                <FileTextIcon className="w-5 h-5 text-violet-electric" aria-hidden="true" data-id="element-190" />
                <span className="text-xs font-mono text-[#8A8A95]" data-id="element-191">
                  {paper.date}
                </span>
              </div>
              <h3 className="text-lg text-[#0A0A0F] font-medium leading-snug mb-3" data-id="element-192">
                {paper.title}
              </h3>
              <p className="text-xs text-[#6A6A75] mb-5" data-id="element-193">{paper.authors}</p>
              <div className="flex flex-wrap gap-2 mb-6" data-id="element-194">
                {paper.tags.map(tag => <span key={tag} className="px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#4A4A55] bg-[#F4F4F6] border border-black/[0.05] rounded" data-id="element-195">
                    {tag}
                  </span>)}
              </div>
              <div className="flex gap-3 pt-4 border-t border-black/[0.06]" data-id="element-196">
                <button className="inline-flex items-center gap-1.5 text-xs text-[#4A4A55] hover:text-violet-deep transition" data-id="element-197">
                  <DownloadIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-198" />
                  {t('papers.download')}
                </button>
                <button className="inline-flex items-center gap-1.5 text-xs text-[#4A4A55] hover:text-violet-deep transition" data-id="element-199">
                  <ExternalLinkIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-200" />
                  {t('papers.viewOnline')}
                </button>
              </div>
            </motion.article>)}
        </div>

        {filtered.length === 0 && <p className="text-center text-[#8A8A95] py-12 text-sm" data-id="element-201">
            No results.
          </p>}
      </div>
    </section>;
}